import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import BASE_URL from "../../../assets/global/url";
import { Row, Col, Form, Button, Modal, Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import swal from "sweetalert";
import { ThreeDot } from "react-loading-indicators";
import NoAccess from "../../../assets/img/NoAccess.png";
import DataTable from "react-data-table-component";
import { customStyles } from "../../../assets/global/table-style";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";
import { PaginationControls } from "../../../hooks/customHook/paginationHook/usePagination";
import { useServerPagination } from "../../../hooks/customHook/paginationHook/useServerPagination";
import { useServerSort } from "../../../hooks/customHook/sortingHook/useTableSort";
import FilterStore from "../../../hooks/customHook/FilterStore";
import { NoTranslate } from "../../../helper/prevent_translate_browser";
import {
  formatToPHP,
  formatToTwoDecimal,
} from "../../../utils/numberFormatter";
import { SUBJECT_ACCOUNT_TYPES } from "./subjectAccountTypes";
import "../../../assets/css/style.css";

// used Module:
// Sidebar > Account List      (/accounts/account-list1)       accountType="accountList"
// Sidebar > Asset Account     (/accounting/assetaccount-list1) accountType="asset"
// Sidebar > Liability Account (/accounting/liabilities1)       accountType="liability"
// Sidebar > Owners' Equity    (/accounting/equity)             accountType="equity"
//
// Replaces the four copied pages (now old*.jsx). App.js mounts it with
// key={accountType} so switching pages from the sidebar starts fresh.

const PER_PAGE_OPTIONS = [10, 25, 50, 100];
const EMPTY_SUBJECT3_FORM = {
  id: null,
  account: "",
  basedCurrency: "",
  investmentAmount: "",
  amount: 0,
};

const toNumber = (value) => parseFloat(String(value ?? "").replace(/,/g, "")) || 0;

// "PHP" -> "₱", "USD" -> "$"; falls back to the code for unknown currencies
const currencySymbol = (code) => {
  try {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
    })
      .formatToParts(0)
      .find((part) => part.type === "currency").value;
  } catch {
    return code ? `${code} ` : "";
  }
};

// 2000, "PHP" -> "₱2,000.00"; -2000 -> "-₱2,000.00"
const formatWithCurrency = (value, code) => {
  const amount = toNumber(value);
  const formatted = `${currencySymbol(code)}${formatToTwoDecimal(Math.abs(amount))}`;
  return amount < 0 ? `-${formatted}` : formatted;
};

// Keeps digits and one decimal point, adds thousands separators
const formatAmountInput = (value) => {
  const [integerPart = "", ...decimals] = value.replace(/[^0-9.]/g, "").split(".");
  const integer = integerPart
    .replace(/^0+(?=\d)/, "")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimals.length ? `${integer}.${decimals.join("")}` : integer;
};

const SubjectAccounts = ({ authrztn, accountType }) => {
  const config = SUBJECT_ACCOUNT_TYPES[accountType];
  const { filter, isEquity } = config;
  const can = (action) => authrztn.includes(`${config.permission}-${action}`);

  const navigate = useNavigate();
  const userLoggedID = useDecodeToken();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [subjectList, setSubjectList] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [currencyList, setCurrencyList] = useState([]);
  const [forexGainLoss, setForexGainLoss] = useState(0);
  const [searchText, setSearchText] = useState(() => filter.getSearch());

  // Subject 2 modal
  const [showSubject2Modal, setShowSubject2Modal] = useState(false);
  const [editingSubject2Id, setEditingSubject2Id] = useState(null);
  const [subject2name, setSubject2name] = useState("");
  const [subject2type, setSubject2type] = useState("Bank");
  const [isSubmittingSubject2, setIsSubmittingSubject2] = useState(false);

  // Subject 3 modal (edit is Owner's Equity only)
  const [showSubject3Modal, setShowSubject3Modal] = useState(false);
  const [subject3Form, setSubject3Form] = useState(EMPTY_SUBJECT3_FORM);
  const [isSubmittingSubject3, setIsSubmittingSubject3] = useState(false);
  const isEditingSubject3 = subject3Form.id !== null;

  // items-shown comes back from the View Account page; otherwise use retention
  const [initialPerPage] = useState(() => {
    const fromUrl = Number(searchParams.get("items-shown"));
    return PER_PAGE_OPTIONS.includes(fromUrl) ? fromUrl : filter.getPerPage();
  });

  const pagination = useServerPagination(
    BASE_URL + config.listUrl,
    initialPerPage,
  );

  // No initial sortBy: the backend default (newest first) has no visible column,
  // so every header starts neutral until the user picks one
  const sort = useServerSort(pagination);

  // Closed until the saved page is restored, so the hook's own reset-to-1
  // (from updateParams) doesn't overwrite the saved page
  const pageRestored = useRef(false);

  const selectedSubjectData = subjectList.find(
    (subject) => subject.id === selectedSubject,
  );

  // ---------- Data loading ----------

  const selectSubject = (subjectId) => {
    setSelectedSubject(subjectId);
    if (subjectId) FilterStore.saveSubject2(subjectId);
    setSearchParams(
      (prev) => {
        if (subjectId) prev.set("subject2", subjectId);
        else prev.delete("subject2");
        return prev;
      },
      { replace: true },
    );
  };

  // Keeps the current Subject 2 when it still exists, otherwise falls back to
  // the URL, then the last one saved, then the first in the list
  const pickSubject = (list) => {
    const exists = (id) => id && list.some((subject) => subject.id === id);
    if (exists(selectedSubject)) return selectedSubject;
    if (exists(searchParams.get("subject2"))) return searchParams.get("subject2");
    if (exists(FilterStore.getSubject2())) return FilterStore.getSubject2();
    return list[0]?.id ?? null;
  };

  const reloadData = () => {
    axios
      .get(BASE_URL + "/accountListSub/getSubject", {
        params: { account_selected: config.moduleType },
      })
      .then((res) => {
        setSubjectList(res.data);
        selectSubject(pickSubject(res.data));
      })
      .catch((err) => console.error("Failed to fetch Subject 2:", err))
      .finally(() => setIsLoading(false));
  };

  const reloadCurrency = () => {
    axios.get(BASE_URL + "/currency/fetchCurrency").then((res) => {
      setCurrencyList(res.data);
    });
  };

  const reloadForexGainLoss = () => {
    axios
      .get(BASE_URL + "/accountListSub/getForexGainLossSubject")
      .then((res) => setForexGainLoss(res.data.forexGainLoss || 0))
      .catch((err) => console.error("Failed to fetch Forex Gain/Loss:", err));
  };

  const reloadAll = () => {
    pagination.refreshData();
    reloadData();
    if (config.showForexGainLoss) reloadForexGainLoss();
  };

  useEffect(() => {
    reloadCurrency();
    reloadData();
    if (config.showForexGainLoss) reloadForexGainLoss();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced Subject 3 fetch on Subject 2 / search change
  useEffect(() => {
    filter.saveSearch(searchText);
    if (!selectedSubject) {
      setIsTableLoading(false);
      return;
    }
    setIsTableLoading(true);

    const timer = setTimeout(() => {
      pagination.updateParams({
        [config.subjectParam]: selectedSubject,
        searchText,
      });

      // First fetch only: restore the saved page after updateParams' reset
      if (!pageRestored.current) {
        const savedPage = filter.getPage();
        setTimeout(() => {
          if (savedPage > 1) pagination.setCurrentPage(savedPage);
          pageRestored.current = true;
        }, 0);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject, searchText]);

  useEffect(() => {
    if (!pagination.loading) setIsTableLoading(false);
  }, [pagination.loading]);

  useEffect(() => {
    if (pageRestored.current && pagination.currentPage) {
      filter.savePage(pagination.currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage]);

  useEffect(() => {
    if (pagination.itemsPerPage) filter.savePerPage(pagination.itemsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.itemsPerPage]);

  // Shared Percentage = investment (in PHP) over the investments on this page,
  // same as the old OwnersEquity.jsx
  const investmentTotal = pagination.data.reduce(
    (sum, item) =>
      sum +
      toNumber(item.investment_amount) * toNumber(item.currency?.currency_rate),
    0,
  );

  const subject3Rows = pagination.data
    .filter((item) => item.account_list_base_sub_id === selectedSubject)
    .map((item) => {
      const convertedInvestment =
        toNumber(item.investment_amount) *
        toNumber(item.currency?.currency_rate);

      return {
        id: item.id,
        account: item.account_name,
        amount: item.amount,
        investment_amount: item.investment_amount,
        currency_id: item.currency_id,
        basedCurrency: item.currency?.currency_name || "",
        sharedPercentage:
          investmentTotal > 0
            ? (convertedInvestment / investmentTotal) * 100
            : 0,
      };
    });

  const subtotalBalances = subjectList.reduce(
    (sum, subject) => sum + toNumber(subject.totalAmount),
    0,
  );
  const netBalance = subtotalBalances + forexGainLoss;

  // ---------- Subject 2 ----------

  const openSubject2Modal = (subject = null) => {
    setEditingSubject2Id(subject?.id ?? null);
    setSubject2name(subject?.subject_name ?? "");
    setSubject2type(subject?.subject_type ?? "Bank");
    setShowSubject2Modal(true);
  };

  const resetSubject2Modal = () => {
    setShowSubject2Modal(false);
    setEditingSubject2Id(null);
    setSubject2name("");
    setSubject2type("Bank");
  };

  const closeSubject2Modal = () => {
    if (!isSubmittingSubject2) resetSubject2Modal();
  };

  const submitSubject2 = async () => {
    const trimmedName = subject2name.trim();
    const isEdit = editingSubject2Id !== null;

    if (trimmedName === "") {
      swal({
        title: "Oops!",
        text: "Please enter a subject name",
        icon: "error",
        timer: 2000,
      });
      return;
    }

    // Button goes into loading mode as soon as the confirmation pops up
    setIsSubmittingSubject2(true);

    try {
      const confirmed = await swal({
        title: "Are you sure?",
        text: isEdit
          ? "Once submitted, it will update the subject"
          : "Once submitted, it will add new subject",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });
      if (!confirmed) return;

      const res = isEdit
        ? await axios.put(
            `${BASE_URL}/accountListSub/updateSubject/${editingSubject2Id}`,
            {
              subject2name: trimmedName,
              subject2type,
              module_type: config.moduleType,
              userLoggedID,
            },
          )
        : await axios.post(BASE_URL + "/accountListSub/addSubject", {
            subject: trimmedName,
            subject_type: subject2type,
            module_type: config.moduleType,
            userLoggedID,
          });

      if (res.status === 200) {
        resetSubject2Modal();
        reloadData();
        swal({
          title: "Success",
          text: isEdit
            ? "Subject updated successfully"
            : "Subject added successfully",
          icon: "success",
          timer: 2000,
        });
      } else if (res.status === 201 || res.status === 202) {
        swal({
          title: "Oops!",
          text: "Subject already exists",
          icon: "error",
          timer: 2000,
        });
      }
    } catch (error) {
      console.error("Error saving Subject 2:", error);
      swal({
        title: "Something went wrong",
        text: "Please contact your support immediately",
        icon: "error",
        timer: 2000,
      });
    } finally {
      setIsSubmittingSubject2(false);
    }
  };

  const deleteSubject2 = async (subject) => {
    const confirmed = await swal({
      icon: "warning",
      title: "Confirm Deletion",
      text: `Are you sure you want to delete "${subject.subject_name}"?`,
      buttons: true,
      dangerMode: true,
    });
    if (!confirmed) return;

    try {
      const res = await axios.delete(
        `${BASE_URL}/accountListSub/deleteSubject2/${subject.id}`,
        { data: { userLoggedID } },
      );

      if (res.status === 200) {
        swal({
          icon: "success",
          title: "Subject 2 Deleted",
          text: "Subject 2 has been deleted successfully.",
        });
        reloadAll();
      } else if (res.status === 202) {
        swal({
          icon: "error",
          title: "Subject 2 cannot delete",
          text: "Subject 2 is currently in use",
        });
      }
    } catch (error) {
      console.error("Error deleting Subject 2:", error);
    }
  };

  // ---------- Subject 3 ----------

  const openSubject3Modal = (row = null) => {
    setSubject3Form(
      row
        ? {
            id: row.id,
            account: row.account,
            basedCurrency: row.currency_id,
            investmentAmount: formatToTwoDecimal(row.investment_amount),
            amount: row.amount,
          }
        : EMPTY_SUBJECT3_FORM,
    );
    setShowSubject3Modal(true);
  };

  const resetSubject3Modal = () => {
    setShowSubject3Modal(false);
    setSubject3Form(EMPTY_SUBJECT3_FORM);
  };

  const closeSubject3Modal = () => {
    if (!isSubmittingSubject3) resetSubject3Modal();
  };

  const submitSubject3 = async () => {
    const trimmedAccount = subject3Form.account.trim();
    const investment = toNumber(subject3Form.investmentAmount);

    if (
      !trimmedAccount ||
      !subject3Form.basedCurrency ||
      (isEquity && !isEditingSubject3 && investment <= 0)
    ) {
      swal({
        title: "Oops!",
        text: "Please fill in all required fields",
        icon: "error",
        timer: 2000,
      });
      return;
    }

    setIsSubmittingSubject3(true);

    try {
      const confirmed = await swal({
        title: "Are you sure?",
        text: isEditingSubject3
          ? `Once submitted, it will update "${trimmedAccount}".`
          : `Once submitted, "${trimmedAccount}" will be added as a new Subject 3 under "${selectedSubjectData?.subject_name || "the selected Subject 2"}".`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });
      if (!confirmed) return;

      // Only the name can change on edit: Last Balance and Investment Amount
      // are sent back as they are (see editSubject3 in accountlist_sub.route.js)
      const res = isEditingSubject3
        ? await axios.post(BASE_URL + "/accountListSub/editSubject3", {
            subject3List: [
              {
                subjectId: selectedSubject,
                subject3Id: subject3Form.id,
                isNew: false,
                account: trimmedAccount,
                amount: subject3Form.amount,
                investmentAmount: investment,
                basedCurrency: subject3Form.basedCurrency,
              },
            ],
            module_type: config.moduleType,
            userLoggedID,
          })
        : await axios.post(BASE_URL + "/accountListSub/addSubject3", {
            subject3List: [
              {
                subjectId: selectedSubject,
                isNew: true,
                account: trimmedAccount,
                amount: isEquity ? investment : "0",
                basedCurrency: subject3Form.basedCurrency,
              },
            ],
            module_type: config.moduleType,
            userLoggedID,
          });

      if (res.status === 200) {
        resetSubject3Modal();
        reloadAll();
        swal({
          title: "Success",
          text: isEditingSubject3
            ? "Subject 3 updated successfully"
            : "Subject 3 added successfully",
          icon: "success",
          timer: 2000,
        });
      } else if (res.status === 201) {
        swal({
          title: "Oops!",
          text: "Subject already exists",
          icon: "warning",
          timer: 2000,
        });
      }
    } catch (error) {
      console.error("Error saving Subject 3:", error);
      swal({
        title: "Something went wrong",
        text: "Please contact your support immediately",
        icon: "error",
        timer: 2000,
      });
    } finally {
      setIsSubmittingSubject3(false);
    }
  };

  const deleteSubject3 = async (row) => {
    const confirmed = await swal({
      icon: "warning",
      title: "Confirm Deletion",
      text: `Are you sure you want to delete "${row.account}"?`,
      buttons: true,
      dangerMode: true,
    });
    if (!confirmed) return;

    try {
      const res = await axios.delete(
        `${BASE_URL}/accountListSub/deleteSubject3/${row.id}`,
        { data: { userLoggedID } },
      );

      if (res.status === 200) {
        swal({
          icon: "success",
          title: "Subject 3 Deleted",
          text: "Subject 3 has been deleted successfully.",
        });
        reloadAll();
      } else if (res.status === 202) {
        swal({
          icon: "error",
          title: "Subject 3 cannot delete",
          text: "Subject 3 already has a transaction.",
        });
      }
    } catch (error) {
      console.error("Error deleting Subject 3:", error);
    }
  };

  const viewSubject3 = (row) => {
    navigate(
      `${config.viewPath}/${row.id}?subject2=${selectedSubject}&items-shown=${pagination.itemsPerPage}`,
      { state: config.viewState },
    );
  };

  // ---------- Table ----------

  const subjectIcon = (subject) =>
    subject?.subject_type === "Bank" ? "fa-building-columns" : "fa-database";

  // Light header instead of the app-wide purple one (table-style.js)
  const tableStyles = {
    ...customStyles,
    headCells: {
      style: {
        ...customStyles.headCells.style,
        backgroundColor: "var(--sa-surface-2)",
        color: "var(--sa-muted)",
        fontWeight: "600",
        fontSize: "14px",
        padding: "14px 8px",
      },
    },
    rows: {
      style: {
        ...customStyles.rows.style,
        padding: "14px 0px",
      },
    },
    // The table draws "Fetching records..." and the empty state in its own
    // white wrapper; follow the page surface so they stay dark in dark mode
    progress: {
      style: {
        ...customStyles.progress?.style,
        backgroundColor: "var(--sa-surface)",
        color: "var(--sa-muted)",
      },
    },
    noData: {
      style: {
        ...customStyles.noData?.style,
        backgroundColor: "var(--sa-surface)",
        color: "var(--sa-muted)",
      },
    },
  };

  const subject3Columns = [
    {
      name: "Account",
      selector: (row) => (
        <NoTranslate>
          <span className="sa-account">
            <span className="sa-account-icon">
              {currencySymbol(row.basedCurrency).trim()}
            </span>
            <span className="sa-account-name">{row.account}</span>
          </span>
        </NoTranslate>
      ),
      sortField: "account_name",
      grow: 2,
      style: { justifyContent: "flex-start", paddingLeft: "24px" },
    },
    {
      name: "Last Balance",
      selector: (row) => (
        <NoTranslate>
          <span
            className={`sa-amount ${toNumber(row.amount) < 0 ? "text-danger" : ""}`}
          >
            {formatWithCurrency(row.amount, row.basedCurrency)}
          </span>
        </NoTranslate>
      ),
      sortField: "amount",
    },
    ...(isEquity
      ? [
          {
            name: "Investment Amount",
            selector: (row) => (
              <NoTranslate>
                <span className="sa-amount">
                  {formatWithCurrency(row.investment_amount, row.basedCurrency)}
                </span>
              </NoTranslate>
            ),
            sortField: "investment_amount",
          },
          {
            name: "Shared %",
            selector: (row) => (
              <NoTranslate>
                <div className="sa-share">
                  <span>{row.sharedPercentage.toFixed(2)} %</span>
                  <div className="sa-share-track">
                    <div
                      className="sa-share-fill"
                      style={{ width: `${Math.min(row.sharedPercentage, 100)}%` }}
                    />
                  </div>
                </div>
              </NoTranslate>
            ),
            sortField: "shared_percentage",
          },
        ]
      : []),
    {
      name: "Actions",
      selector: (row) => (
        <div className="sa-row-actions" onClick={(e) => e.stopPropagation()}>
          {can("Edit") && (
            <button
              type="button"
              className="sa-tile-btn"
              title="View account"
              onClick={() => viewSubject3(row)}
            >
              <i className="fa-solid fa-eye"></i>
            </button>
          )}
          {isEquity && can("Edit") && (
            <button
              type="button"
              className="sa-tile-btn"
              title="Edit account"
              onClick={() => openSubject3Modal(row)}
            >
              <i className="fa-solid fa-pen"></i>
            </button>
          )}
          {can("Delete") && (
            <button
              type="button"
              className="sa-tile-btn sa-tile-btn-danger"
              title="Delete account"
              onClick={() => deleteSubject3(row)}
            >
              <i className="fa-solid fa-trash"></i>
            </button>
          )}
        </div>
      ),
    },
  ];

  const tableLoader = (
    <div className="sa-table-state">
      <ThreeDot
        variant="brick-stack"
        color="#6290FE"
        size="small"
        text="Fetching records..."
        textColor="#6c757d"
      />
    </div>
  );

  const tableEmpty = (
    <div className="sa-table-state">
      <i className="fa-regular fa-folder-open"></i>
      <span>
        {searchText
          ? `No accounts match "${searchText}"`
          : "No Subject 3 accounts yet"}
      </span>
    </div>
  );

  // ---------- Render ----------

  if (isLoading) {
    return (
      <div className="h-100 w-100 border bg-white custom-container">
        <div className="loading-container">
          <ThreeDot
            variant="brick-stack"
            color="#6290FE"
            size="large"
            text="Loading Data..."
            textColor=""
          />
        </div>
      </div>
    );
  }

  if (!can("View")) {
    return (
      <div className="h-100 w-100 border bg-white custom-container">
        <div className="no-access">
          <img src={NoAccess} alt="NoAccess" className="no-access-img" />
          <h3>You don't have access to this function.</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="h-100 w-100 border custom-container sa-page">
      {/* Header */}
      <div className="sa-header">
        <div className="d-flex align-items-center gap-3">
          <div className="sa-header-icon">
            <i className={`fa-solid ${config.icon}`}></i>
          </div>
          <div className="title-custom">
            <h1 className="sa-title">{config.title}</h1>
            <p className="sa-subtitle">{config.description}</p>
          </div>
        </div>

        {subjectList.length > 0 && (
          <label className="sa-subject-picker">
            <span className="sa-tile-icon">
              <i className="fa-solid fa-layer-group"></i>
            </span>
            <span className="sa-subject-picker-body">
              <span className="sa-subject-picker-label">Subject 2</span>
              <NoTranslate>
                <select
                  value={selectedSubject ?? ""}
                  onChange={(e) => selectSubject(e.target.value)}
                >
                  {subjectList.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.subject_name}
                    </option>
                  ))}
                </select>
              </NoTranslate>
            </span>
            <i className="fa-solid fa-chevron-down sa-subject-picker-caret"></i>
          </label>
        )}
      </div>

      <Row className="g-4">
        {/* Subject 2 + balances footer */}
        <Col xl={4} lg={5} xs={12}>
          <div className="sa-card h-100">
            <div className="sa-card-header">
              <span className="sa-card-title">Subject 2</span>
              {can("Add") && (
                <Button
                  variant="primary"
                  className="sa-primary-btn"
                  onClick={() => openSubject2Modal()}
                >
                  <i className="fa-solid fa-plus me-2"></i>Add
                </Button>
              )}
            </div>

            {subjectList.length === 0 ? (
              <div className="sa-empty">
                <i className="fa-regular fa-folder-open"></i>
                <span>No Subject 2 yet</span>
              </div>
            ) : (
              <NoTranslate>
                <ul className="sa-subject-list">
                  {subjectList.map((subject) => {
                    const isLocked = subject.id === config.lockedSubjectId;
                    const isActive = subject.id === selectedSubject;

                    return (
                      <li
                        key={subject.id}
                        className={`sa-subject-item ${isActive ? "active" : ""}`}
                        onClick={() => selectSubject(subject.id)}
                      >
                        <span className="sa-tile-icon">
                          <i className={`fa-solid ${subjectIcon(subject)}`}></i>
                        </span>
                        <div className="sa-subject-info">
                          <span className="sa-subject-name">
                            {subject.subject_name}
                            {isLocked && (
                              <i
                                className="fa-solid fa-lock sa-lock"
                                title="System Subject 2"
                              ></i>
                            )}
                          </span>
                          <span className="sa-type-badge">
                            {subject.subject_type}
                          </span>
                          <span
                            className={`sa-subject-balance ${
                              toNumber(subject.totalAmount) < 0
                                ? "text-danger"
                                : ""
                            }`}
                          >
                            {formatToPHP(subject.totalAmount)}
                          </span>
                        </div>
                        {!isLocked && (
                          <div
                            className="sa-subject-actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {can("Edit") && (
                              <button
                                type="button"
                                className="sa-icon-btn"
                                title="Edit Subject 2"
                                onClick={() => openSubject2Modal(subject)}
                              >
                                <i className="fa-solid fa-pen"></i>
                              </button>
                            )}
                            {can("Delete") && (
                              <button
                                type="button"
                                className="sa-icon-btn sa-icon-btn-danger"
                                title="Delete Subject 2"
                                onClick={() => deleteSubject2(subject)}
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            )}
                          </div>
                        )}
                        <i className="fa-solid fa-chevron-right sa-chevron"></i>
                      </li>
                    );
                  })}
                </ul>
              </NoTranslate>
            )}

            {/* Footer: totals of every Subject 2 above */}
            {subjectList.length > 0 && (
              <NoTranslate>
                <div className="sa-balance-footer">
                  <div className="sa-balance-row">
                    <span>Subtotal</span>
                    <span
                      className={`sa-balance-value ${
                        subtotalBalances < 0 ? "text-danger" : "text-success"
                      }`}
                    >
                      {formatToPHP(subtotalBalances)}
                    </span>
                  </div>

                  {config.showForexGainLoss && (
                    <div className="sa-balance-row sa-balance-forex">
                      <span className="d-flex align-items-center gap-2">
                        <i className="fa-solid fa-right-left sa-forex-icon"></i>
                        <span>
                          Foreign Exchange Gain/Loss
                          <i
                            className="fa-solid fa-circle-info sa-info"
                            title="Gain or loss from currency rate changes on foreign currency accounts"
                          ></i>
                        </span>
                      </span>
                      <span
                        className={`sa-balance-value ${
                          forexGainLoss < 0 ? "text-danger" : "sa-forex-value"
                        }`}
                      >
                        {formatToPHP(forexGainLoss)}
                      </span>
                    </div>
                  )}

                  <div className="sa-balance-row sa-balance-net">
                    <span className="d-flex align-items-center gap-2">
                      <span className="sa-tile-icon sa-tile-icon-sm">
                        <i className="fa-solid fa-database"></i>
                      </span>
                      {config.showForexGainLoss ? "Net Balance" : "Total Balance"}
                    </span>
                    <span className="sa-balance-value text-primary">
                      {formatToPHP(
                        config.showForexGainLoss ? netBalance : subtotalBalances,
                      )}
                    </span>
                  </div>
                </div>
              </NoTranslate>
            )}
          </div>
        </Col>

        {/* Subject 3 */}
        <Col xl={8} lg={7} xs={12}>
          <div className="sa-card h-100">
            <div className="sa-panel-head">
              <span className="sa-tile-icon sa-tile-icon-lg">
                <i className={`fa-solid ${subjectIcon(selectedSubjectData)}`}></i>
              </span>
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <span className="sa-card-title">Subject 3</span>
                  {selectedSubjectData && (
                    <span className="sa-card-caption">
                      <NoTranslate>{selectedSubjectData.subject_name}</NoTranslate>
                    </span>
                  )}
                </div>
                <span className="sa-subtitle">
                  Manage and view accounts under this subject
                </span>
              </div>
              {can("Add") && (
                <Button
                  variant="primary"
                  className="sa-primary-btn"
                  disabled={!selectedSubjectData}
                  title={!selectedSubjectData ? "Select a Subject 2 first" : undefined}
                  onClick={() => openSubject3Modal()}
                >
                  <i className="fa-solid fa-plus me-2"></i>Add Account
                </Button>
              )}
            </div>

            <div className="sa-search">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input
                type="text"
                className="form-control"
                placeholder="Search account..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button
                  type="button"
                  className="sa-search-clear"
                  title="Clear search"
                  onClick={() => setSearchText("")}
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              )}
            </div>

            <DataTable
              columns={sort.sortColumns(subject3Columns)}
              data={subject3Rows}
              customStyles={tableStyles}
              className="dataTable"
              progressPending={isTableLoading}
              progressComponent={tableLoader}
              noDataComponent={tableEmpty}
              onRowClicked={can("Edit") ? viewSubject3 : undefined}
              highlightOnHover
              {...sort.sortProps}
            />
            <div className="sa-pagination">
              <PaginationControls {...pagination} />
            </div>
          </div>
        </Col>
      </Row>

      {/* Subject 3 Modal */}
      <Modal
        show={showSubject3Modal}
        onHide={closeSubject3Modal}
        backdrop="static"
        size="lg"
        centered
      >
        <Modal.Header className="border-0">
          <Modal.Title>
            {isEditingSubject3 ? "Edit Subject 3" : "Add Subject 3"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={12}>
              <Form.Label>Subject 2</Form.Label>
              <Form.Control
                type="text"
                value={selectedSubjectData?.subject_name || ""}
                readOnly
              />
            </Col>
            <Col sm={6}>
              <Form.Label>
                Account Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Account Name"
                value={subject3Form.account}
                disabled={isSubmittingSubject3}
                onChange={(e) =>
                  setSubject3Form({ ...subject3Form, account: e.target.value })
                }
              />
            </Col>
            <Col sm={6}>
              <Form.Label>
                Currency <span className="text-danger">*</span>
              </Form.Label>
              {/* Currency can't change after creation (Last Balance is in it) */}
              <Form.Select
                value={subject3Form.basedCurrency}
                disabled={isSubmittingSubject3 || isEditingSubject3}
                onChange={(e) =>
                  setSubject3Form({
                    ...subject3Form,
                    basedCurrency: e.target.value,
                  })
                }
              >
                <option value="" disabled>
                  Select Currency
                </option>
                {currencyList.map((currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.currency_name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12}>
              {isEquity ? (
                <>
                  <Form.Label>
                    Investment Amount <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={subject3Form.investmentAmount}
                    readOnly={isEditingSubject3}
                    disabled={isSubmittingSubject3}
                    onChange={(e) =>
                      setSubject3Form({
                        ...subject3Form,
                        investmentAmount: formatAmountInput(e.target.value),
                      })
                    }
                  />
                  {isEditingSubject3 && (
                    <Form.Text muted>
                      Investment Amount can't be changed once the account is
                      created.
                    </Form.Text>
                  )}
                </>
              ) : (
                <>
                  <Form.Label>Amount</Form.Label>
                  <Form.Control type="text" value="0" readOnly />
                  <Form.Text muted>Amount is automatically set to 0</Form.Text>
                </>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeSubject3Modal}
            disabled={isSubmittingSubject3}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={submitSubject3}
            disabled={
              !subject3Form.account.trim() ||
              !subject3Form.basedCurrency ||
              isSubmittingSubject3
            }
          >
            {isSubmittingSubject3 ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                {isEditingSubject3 ? "Updating..." : "Adding..."}
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Subject 2 Modal */}
      <Modal
        show={showSubject2Modal}
        onHide={closeSubject2Modal}
        backdrop="static"
        size="md"
        centered
      >
        <Modal.Header className="border-0">
          <Modal.Title>
            {editingSubject2Id ? "Edit Subject 2" : "Add New Subject 2"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={12}>
              <Form.Label>
                Subject Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Subject Name"
                value={subject2name}
                disabled={isSubmittingSubject2}
                onChange={(e) => setSubject2name(e.target.value)}
              />
            </Col>
            <Col xs={12}>
              <Form.Label>
                Subject Type <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={subject2type}
                disabled={isSubmittingSubject2}
                onChange={(e) => setSubject2type(e.target.value)}
              >
                <option value="Bank">Bank</option>
                <option value="Cash">Cash</option>
              </Form.Select>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeSubject2Modal}
            disabled={isSubmittingSubject2}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={submitSubject2}
            disabled={!subject2name.trim() || isSubmittingSubject2}
          >
            {isSubmittingSubject2 ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                {editingSubject2Id ? "Updating..." : "Adding..."}
              </>
            ) : editingSubject2Id ? (
              "Update"
            ) : (
              "Add"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SubjectAccounts;
